import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, VStack, HStack, Text, Spinner, Badge, Flex, IconButton, Input,
    Table, Thead, Tbody, Tr, Th, Td, useToast, Tag, TagLabel, Wrap, WrapItem,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';
import axios from 'axios';

const API_URL = process.env.REACT_APP_AWS_API_URL || '';

// Default to the current UTC month as 'YYYY-MM'.
function currentMonth() {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export default function SmsUsageSection({ platformKey, onAuthError }) {
    const [month, setMonth] = useState(currentMonth());
    const [tenants, setTenants] = useState([]);
    const [grandTotal, setGrandTotal] = useState(0);
    const [grandOverageCost, setGrandOverageCost] = useState(0);
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    const headers = { 'x-platform-key': platformKey };

    const fetchUsage = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/platform/sms-usage`, {
                params: { month },
                headers,
            });
            const data = res.data || {};
            setTenants(data.tenants || []);
            setGrandTotal(data.grandTotal || 0);
            setGrandOverageCost(data.grandOverageCost || 0);
        } catch (err) {
            if (err?.response?.status === 401 || err?.response?.status === 403) {
                onAuthError && onAuthError();
            } else {
                toast({ title: 'Failed to load SMS usage', status: 'error', duration: 3000 });
            }
        } finally {
            setLoading(false);
        }
    }, [month, platformKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { if (platformKey) fetchUsage(); }, [fetchUsage, platformKey]);

    // Tenants with usage first, highest volume on top.
    const sorted = [...tenants].sort((a, b) => (b.monthTotal || 0) - (a.monthTotal || 0));

    return (
        <VStack spacing={4} align="stretch">
            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                <HStack>
                    <Text fontWeight="600" color="gray.700">SMS usage for</Text>
                    <Input
                        type="month"
                        size="sm"
                        maxW="170px"
                        value={month}
                        onChange={(e) => setMonth(e.target.value || currentMonth())}
                    />
                    <Badge colorScheme="orange" fontSize="sm" px={2} py={1} borderRadius="md">
                        {grandTotal} texts total
                    </Badge>
                    <Badge colorScheme={grandOverageCost > 0 ? 'red' : 'green'} fontSize="sm" px={2} py={1} borderRadius="md">
                        ${grandOverageCost.toFixed(2)} billable
                    </Badge>
                </HStack>
                <IconButton icon={<FiRefreshCw />} size="sm" variant="ghost" onClick={fetchUsage} aria-label="Refresh" />
            </Flex>

            <Text fontSize="xs" color="gray.500">
                Counts include texts sent and texts queued for morning delivery. Use these
                numbers to bill each tenant by volume. OTP / login texts are not counted here.
            </Text>

            {loading ? (
                <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
            ) : sorted.length === 0 ? (
                <Box textAlign="center" py={10} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100">
                    <Text color="gray.400">No tenants found.</Text>
                </Box>
            ) : (
                <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100" overflowX="auto">
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th>Tenant</Th>
                                <Th isNumeric>This month</Th>
                                <Th isNumeric>Free</Th>
                                <Th isNumeric>Over</Th>
                                <Th isNumeric>Est. $</Th>
                                <Th>By category</Th>
                                <Th isNumeric>Lifetime</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {sorted.map((t) => {
                                const cats = Object.entries(t.byCategory || {})
                                    .sort((a, b) => b[1] - a[1]);
                                return (
                                    <Tr key={t.laundryId}>
                                        <Td>
                                            <Text fontWeight="500">{t.laundryName || t.laundryId}</Text>
                                            <HStack spacing={1} mt={1}>
                                                <Text fontSize="10px" color="gray.400" fontFamily="mono">{t.laundryId}</Text>
                                                {!t.smsEnabled && (
                                                    <Badge colorScheme="gray" fontSize="9px">SMS off</Badge>
                                                )}
                                            </HStack>
                                        </Td>
                                        <Td isNumeric fontWeight="600" color={t.monthTotal > 0 ? 'orange.600' : 'gray.400'}>
                                            {t.monthTotal || 0}
                                        </Td>
                                        <Td isNumeric color="gray.500">{(t.freeAllowance || 0).toLocaleString()}</Td>
                                        <Td isNumeric color={(t.overageCount || 0) > 0 ? 'red.600' : 'gray.400'} fontWeight={(t.overageCount || 0) > 0 ? '600' : '400'}>
                                            {t.overageCount || 0}
                                        </Td>
                                        <Td isNumeric color={(t.overageCost || 0) > 0 ? 'red.600' : 'gray.400'} fontWeight={(t.overageCost || 0) > 0 ? '600' : '400'}>
                                            ${(t.overageCost || 0).toFixed(2)}
                                        </Td>
                                        <Td>
                                            {cats.length === 0 ? (
                                                <Text fontSize="xs" color="gray.400">—</Text>
                                            ) : (
                                                <Wrap spacing={1}>
                                                    {cats.map(([cat, count]) => (
                                                        <WrapItem key={cat}>
                                                            <Tag size="sm" colorScheme="orange" variant="subtle">
                                                                <TagLabel fontSize="10px">{cat}: {count}</TagLabel>
                                                            </Tag>
                                                        </WrapItem>
                                                    ))}
                                                </Wrap>
                                            )}
                                        </Td>
                                        <Td isNumeric color="gray.500">{t.lifetimeCount || 0}</Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            )}
        </VStack>
    );
}
